import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, Columns2, FileText, GitBranch } from 'lucide-react';
import { VERSIONS, resolveVersion, type VersionStatus } from '@/versions/registry';
import { ChromeDropLeft } from './ChromeDropLeft';

const STATUS_LABEL: Record<VersionStatus, string> = {
  live: 'Live',
  draft: 'Draft',
  archived: 'Archived',
  locked: 'Locked',
};

// Open the target version in a right-half popup so the user gets a real
// side-by-side comparison rather than a same-window switch. Window features
// (width/height) push browsers into popup mode; resizing the current window
// to the left half is best-effort because some browsers silently block
// resizeTo for tabbed windows. If the resize is blocked, the user can still
// drag manually — the popup arrives positioned regardless.
function openSideBySide(targetVersionId: string) {
  const sw = window.screen.availWidth ?? window.innerWidth;
  const sh = window.screen.availHeight ?? window.innerHeight;
  const halfW = Math.max(640, Math.floor(sw / 2));

  const target = new URL(window.location.href);
  target.searchParams.set('v', targetVersionId);

  // Named so a second click on the same row brings the existing popup back
  // into focus instead of opening a third window.
  window.open(
    target.toString(),
    `mock-ui-compare-${targetVersionId}`,
    `width=${halfW},height=${sh},left=${sw - halfW},top=0`,
  );

  try {
    window.resizeTo(halfW, sh);
    window.moveTo(0, 0);
  } catch {
    // Blocked by the browser; nothing to do.
  }
}

export function VersionPicker({ compact = false }: { compact?: boolean }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const active = resolveVersion(params.get('v'));
  const [open, setOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function switchTo(versionId: string) {
    const next = new URLSearchParams(params);
    next.set('v', versionId);
    setParams(next, { replace: false });
    setOpen(false);
  }

  function goToChangelog(versionId: string) {
    setOpen(false);
    navigate(`/changelog#${versionId}`);
  }

  function startSideBySide(versionId: string) {
    setOpen(false);
    openSideBySide(versionId);
  }

  const versionMenuBody = VERSIONS.map((v) => {
    const isActive = v.id === active.id;
    const isHovered = hoveredRow === v.id && !isActive;
    return (
      <div
        key={v.id}
        onMouseEnter={() => setHoveredRow(v.id)}
        onMouseLeave={() => setHoveredRow((prev) => (prev === v.id ? null : prev))}
        style={{
          borderRadius: 6,
          background: isActive
            ? 'var(--hairline)'
            : isHovered
              ? 'var(--surface-2)'
              : 'transparent',
          marginBottom: 2,
          padding: '8px 10px',
          transition: 'background 120ms ease, color 120ms ease',
        }}
      >
        <button
          type="button"
          onClick={() => switchTo(v.id)}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span
              style={{
                color: 'var(--ink-primary)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {v.label}
            </span>
            <span
              className="stage-chip tabular"
              style={{
                fontSize: 9,
                opacity: v.status === 'live' ? 1 : 0.7,
              }}
            >
              {STATUS_LABEL[v.status]}
            </span>
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-tertiary)',
              marginTop: 2,
            }}
          >
            Created {v.dateCreated}
          </div>
        </button>

        <div
          style={{
            display: 'flex',
            gap: 4,
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid var(--hairline)',
          }}
        >
          <VersionAction
            keyId={`${v.id}:changelog`}
            icon={<FileText size={12} aria-hidden="true" />}
            label="Changelog"
            title={`View ${v.label} changelog`}
            hovered={hoveredAction === `${v.id}:changelog`}
            onHoverChange={setHoveredAction}
            onClick={() => goToChangelog(v.id)}
          />
          <VersionAction
            keyId={`${v.id}:sxs`}
            icon={<Columns2 size={12} aria-hidden="true" />}
            label="Side-by-side"
            title={`Open ${v.label} in a right-half window; current view shrinks to the left half (best-effort).`}
            hovered={hoveredAction === `${v.id}:sxs`}
            onHoverChange={setHoveredAction}
            onClick={() => startSideBySide(v.id)}
          />
        </div>
      </div>
    );
  });

  return (
    <div data-comment-anchor="VersionPicker" className="relative" ref={rootRef}>
      <button
        type="button"
        className={compact ? 'theme-toggle tt-left' : 'switcher-trigger'}
        data-review-mode="true"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={compact ? `Switch UI version · ${active.id}` : undefined}
        title={compact ? undefined : 'Switch UI version'}
      >
        <GitBranch size={14} style={{ color: 'var(--ink-secondary)' }} aria-hidden="true" />
        {!compact ? (
          <>
            <span style={{ color: 'var(--ink-primary)', fontSize: '13px' }}>{active.id}</span>
            <ChevronDown size={14} style={{ color: 'var(--ink-tertiary)' }} aria-hidden="true" />
          </>
        ) : null}
      </button>
      {open && (
        compact ? (
          <ChromeDropLeft
            open={open}
            anchorRef={rootRef}
            style={{ width: '320px', padding: '6px' }}
          >
            {versionMenuBody}
          </ChromeDropLeft>
        ) : (
          <div
            className="glass-deep"
            style={{
              position: 'absolute',
              top: '36px',
              right: 0,
              width: '320px',
              maxHeight: '70vh',
              overflowY: 'auto',
              padding: '6px',
              zIndex: 70,
            }}
            role="menu"
          >
            {versionMenuBody}
          </div>
        )
      )}
    </div>
  );
}

// Per-row action button (Changelog / Side-by-side). Hover treatment matches
// the canonical `.persona-row:hover` recipe: background `var(--surface-2)`,
// foreground `var(--ink-primary)`, 120ms ease. Kept in this file because the
// hover-key plumbing is local to the picker.
function VersionAction({
  keyId,
  icon,
  label,
  title,
  hovered,
  onHoverChange,
  onClick,
}: {
  keyId: string;
  icon: ReactNode;
  label: string;
  title: string;
  hovered: boolean;
  onHoverChange: (k: string | null) => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => onHoverChange(keyId)}
      onMouseLeave={() => onHoverChange(null)}
      title={title}
      aria-label={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        flex: 1,
        background: hovered ? 'var(--surface-2)' : 'transparent',
        color: hovered ? 'var(--ink-primary)' : 'var(--ink-secondary)',
        border: 'none',
        cursor: 'pointer',
        fontSize: 11.5,
        padding: '4px 6px',
        borderRadius: 4,
        textAlign: 'left',
        transition: 'background 120ms ease, color 120ms ease',
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
